import moment from 'moment';

export const REMOTE_SUPPORT_REASONS = {
	PLAN: 'plan',
	OVERRIDE: 'override',
	PLAN_DISALLOWS: 'planDisallows',
	NO_MAINTENANCE: 'noMaintenance'
};

export const REMOTE_SUPPORT_STATUS = {
	LOADING: 'loading',
	FAILED: 'failed',
	READY: 'ready'
};

export const REMOTE_SUPPORT_OVERRIDE_DAYS = 7;

export const OVERRIDE_REASON_MAX_LENGTH = 1000;

export const REMOTE_SUPPORT_LOAD_ERROR_MESSAGE = 'The remote support status could not be loaded.';

export const REMOTE_SUPPORT_ALLOW_ERROR_MESSAGE = 'Remote support could not be allowed.';

export const REMOTE_SUPPORT_TOGGLE_ERROR_MESSAGE = 'The remote support setting could not be saved.';

/*
 * Every write on these routes answers 202 with a job. Sixty seconds is the same budget
 * `pollJobUntilTerminal` defaults to; it is named here so the toggle and the override dialog cannot
 * drift apart.
 */
export const REMOTE_SUPPORT_JOB_POLL = {
	intervalMs: 1000,
	maxAttempts: 60
};

/*
 * The list endpoint is paged like every rest-api list. The table shows every type at once, so the
 * flags are read page by page until the pagination says there are no more.
 */
export const MAINTENANCE_PLANS_PAGE_SIZE = 100;

const isBoolean = (value) => {
	return typeof value === 'boolean';
};

/*
 * A status is usable only when the server said whether the tunnel is allowed. Anything else (an
 * empty body, a proxy page, an older rest-api) is reported as a failed load, so the card never
 * offers "Allow" on top of a guess.
 */
export const isRemoteSupportStatus = (status) => {
	return Boolean(status) && typeof status === 'object' && isBoolean(status.allowed);
};

/*
 * `allowedUntil` is UTC. For `plan` it is the maintenance's last day, a calendar date, so it is
 * shown as that day without a timezone shift. For `override` it is an instant (granted + 7 days),
 * so it is shown in the viewer's local time with the hour, as the tunnel closes at that moment.
 */
export const formatAllowedUntil = (status) => {
	const value = status?.allowedUntil;

	if (typeof value !== 'string' || !value.trim()) {
		return null;
	}

	if (status.reason === REMOTE_SUPPORT_REASONS.OVERRIDE) {
		const instant = moment(value, moment.ISO_8601, true);

		return instant.isValid() ? instant.format('DD/MM/YYYY HH:mm') : null;
	}

	const day = moment.utc(value, moment.ISO_8601, true);

	return day.isValid() ? day.format('DD/MM/YYYY') : null;
};

const explanationFor = (status) => {
	switch (status.reason) {
		case REMOTE_SUPPORT_REASONS.PLAN:
			return 'The maintenance type includes remote support.';
		case REMOTE_SUPPORT_REASONS.OVERRIDE:
			return 'Allowed by Syneto Support.';
		case REMOTE_SUPPORT_REASONS.PLAN_DISALLOWS:
			return 'The maintenance type does not include remote support.';
		case REMOTE_SUPPORT_REASONS.NO_MAINTENANCE:
			return 'The maintenance is not active.';
		default:
			return null;
	}
};

/*
 * Turns the server's status into what the card says. `allowed` and `reason` are taken as computed;
 * the card never re-derives the rule from `planAllows` and `maintenanceActive`. When the server
 * says it is not allowed and gives no known reason, the two flags are read only to explain why,
 * so an unknown reason still produces a sentence.
 */
export const describeRemoteSupportStatus = (status) => {
	if (!isRemoteSupportStatus(status)) {
		return null;
	}

	let explanation = explanationFor(status);

	if (!explanation && !status.allowed) {
		if (status.maintenanceActive === false) {
			explanation = 'The maintenance is not active.';
		} else if (status.planAllows === false) {
			explanation = 'The maintenance type does not include remote support.';
		}
	}

	return {
		allowed: status.allowed,
		summary: status.allowed ? 'Remote support is allowed' : 'Remote support is not allowed',
		explanation,
		allowedUntil: status.allowed ? formatAllowedUntil(status) : null,
		canAllow: !status.allowed
	};
};

/*
 * The reason lands in an audit column: the API rejects a blank one and anything over 1,000
 * characters. The dialog checks the same two rules so a refused save is not the first feedback.
 */
export const validateOverrideReason = (reason) => {
	const text = typeof reason === 'string' ? reason : '';

	if (!text.trim()) {
		return 'State why remote support is being allowed.';
	}

	if (text.length > OVERRIDE_REASON_MAX_LENGTH) {
		return `The reason must be at most ${OVERRIDE_REASON_MAX_LENGTH.toLocaleString('en-US')} characters.`;
	}

	return null;
};

/*
 * Reduces the pages of `GET /v1/licensing/maintenance-plans` to `{ [id]: remoteSupportAllowed }`.
 * Items without an id or a boolean flag are skipped rather than shown as off: a missing flag
 * means the switch stays disabled, which is more honest than a wrong value that one click flips.
 */
export const remoteSupportFlagsFromItems = (items) => {
	if (!Array.isArray(items)) {
		return {};
	}

	return items.reduce((flags, item) => {
		if (item && item.id !== undefined && item.id !== null && isBoolean(item.remoteSupportAllowed)) {
			flags[String(item.id)] = item.remoteSupportAllowed;
		}

		return flags;
	}, {});
};
